import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UserPlus, AlertCircle, BookOpen, Code, Zap, Rocket, ClipboardList, MessagesSquare } from "lucide-react";
import { detectSurface } from "../utils/surfaceDetection";

/** The invite link is sent from Docs and from Enable; each lands on its own host
 * and must read as that product, not the other one. */
const INVITE_COPY = {
  docs: {
    label: "Docs",
    footer: "Joveo Developer Portal",
    headline: "Everything you need to integrate and build with Joveo.",
    features: [
      { icon: BookOpen, title: "Platform guides", desc: "Step-by-step walkthroughs for every integration" },
      { icon: Code, title: "API reference", desc: "Complete endpoint docs with live examples" },
      { icon: Zap, title: "Quick start", desc: "Go from zero to your first API call in minutes" },
    ],
  },
  enable: {
    label: "Enable",
    footer: "Joveo Enable",
    headline: "Onboarding, without the back and forth.",
    features: [
      { icon: Rocket, title: "Guided setup", desc: "Every step of go-live in one place" },
      { icon: ClipboardList, title: "Clear requests", desc: "Know exactly what's needed and when" },
      { icon: MessagesSquare, title: "Talk to your team", desc: "Questions answered in context" },
    ],
  },
} as const;

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const surface = detectSurface();
  const copy = surface.appSlug === "enable" ? INVITE_COPY.enable : INVITE_COPY.docs;
  // cortex.joveo.ai keeps its invite page exactly as it was; the partner hosts
  // get a legible wordmark and no talk of a "Joveo account" they don't have.
  const partnerHost = surface.isDedicatedSurface;
  const panelLogoClass = partnerHost ? "h-8 brightness-0 invert opacity-90" : "h-8 drop-shadow-md";
  const navigate = useNavigate();
  const [invite, setInvite] = useState<{ email: string; userType: string } | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/auth/invite/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Invalid invite");
        }
        return res.json();
      })
      .then(setInvite)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Full name is required"); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/auth/invite/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to accept invite");
      }
      await res.json();
      // Accepting an invite creates the account and applies its grants; it no
      // longer hands back a session. Proving who you are is Atlas's job, so
      // send them through the one door.
      navigate("/login", { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2.5 text-sm text-slate-400">
          <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading invite...
        </div>
      </div>
    );
  }

  // Invalid / expired invite
  if (!invite) {
    return (
      <div className="min-h-screen flex bg-white">
        <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] shrink-0 flex-col justify-between p-10 relative overflow-hidden"
          style={{ background: "linear-gradient(145deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)" }}>
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)", backgroundSize: "24px 24px" }} />
          <div className="relative z-10">
            <div className="flex items-center gap-2.5">
              <img src="/joveo-logo.png" alt="Joveo" className={panelLogoClass} />
              <span className="text-[15px] font-medium text-white/60 select-none tracking-wide relative top-[-1px]">{copy.label}</span>
            </div>
          </div>
          <div className="relative z-10" />
          <div className="relative z-10"><p className="text-[11px] text-indigo-300/50">{copy.footer}</p></div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-sm">
            <AlertCircle className="size-10 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Invite not found</h2>
            <p className="text-sm text-slate-500">{error || "This invite is no longer valid or has already been used."}</p>
          </div>
        </div>
      </div>
    );
  }

  // Main invite accept form
  return (
    <div className="min-h-screen flex bg-white">
      {/* Left panel — Joveo branding (matches docs login) */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] shrink-0 flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: "linear-gradient(145deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)" }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="absolute -bottom-32 -right-32 w-[400px] h-[400px] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #818cf8, transparent 70%)" }} />

        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-1">
            <img src="/joveo-logo.png" alt="Joveo" className={panelLogoClass} />
            <span className="text-[15px] font-medium text-white/60 select-none tracking-wide relative top-[-1px]">{copy.label}</span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <p className="text-[28px] leading-[1.3] font-semibold text-white/95 max-w-[360px]">
            {copy.headline}
          </p>
          <div className="space-y-4">
            {copy.features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="mt-0.5 size-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <Icon className="size-3.5 text-indigo-200" />
                </div>
                <div>
                  <div className="text-[13px] font-medium text-white/90">{title}</div>
                  <div className="text-[12px] text-indigo-200/70 mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-[11px] text-indigo-300/50">{copy.footer}</p>
        </div>
      </div>

      {/* Right panel — invite form */}
      <div className="flex-1 flex items-center justify-center px-6 relative bg-slate-50/80">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-[0.05] pointer-events-none" style={{ background: "radial-gradient(circle, #6366f1, transparent 70%)" }} />

        <div className="w-full max-w-[380px] relative z-10">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <img src="/joveo-logo.png" alt="Joveo" className="h-9" />
              <span className="text-[15px] font-medium text-slate-400 select-none tracking-wide">{copy.label}</span>
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-200/50 px-7 py-8">

          <h1 className="text-[18px] font-semibold text-slate-800 text-center mb-6">Create Profile</h1>

          {/* Email (read-only) */}
          <div className="mb-5">
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Email</label>
            <input type="email" value={invite.email} disabled
              className="w-full h-10 px-3.5 text-sm rounded-xl border border-slate-200 bg-slate-50 text-slate-600 cursor-default focus:outline-none" />
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 mb-5 rounded-lg text-xs bg-red-50 border border-red-100 text-red-600 text-left">
              <AlertCircle className="size-3.5 shrink-0" />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Full Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. John Doe"
                className="w-full h-10 px-3.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" />
            </div>

            <p className="text-[11px] text-slate-400 pt-3 border-t border-slate-100">
              {partnerHost
                ? "Next, you'll sign in with this email address — there's no password to set."
                : "You'll sign in with your Joveo account — there's no password to set."}
            </p>

            <button type="submit" disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-white rounded-xl bg-indigo-500 hover:bg-indigo-600 transition-all disabled:opacity-50 cursor-pointer">
              <UserPlus className="size-4" />{submitting ? "Creating account..." : "Create Account"}
            </button>
          </form>

          </div>{/* end card */}

          <p className="text-center text-[11px] text-slate-300 mt-6">
            Need help? Reach out to your Joveo contact.
          </p>
        </div>
      </div>
    </div>
  );
}
